import React, { useState, useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Wrapper, Chip, ChipLabel, ChipRemoveButton, Input } from './styles'

export const ChipsInput = ({
  value,
  onChange,
  defaultValue,
  placeholder,
  disabled,
  readOnly,
  name,
  ...rest
}) => {
  const isControlled = value !== undefined
  const [internalValue, setInternalValue] = useState(defaultValue || [])
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef(null)

  const currentValue = isControlled ? value : internalValue

  useEffect(() => {
    if (!isControlled && defaultValue !== undefined) {
      setInternalValue(defaultValue)
    }
  }, [defaultValue, isControlled])

  const updateValue = useCallback((newValue) => {
    if (!isControlled) {
      setInternalValue(newValue)
    }
    if (onChange) {
      onChange(newValue)
    }
  }, [isControlled, onChange])

  const addChip = useCallback(() => {
    const trimmed = inputValue.trim()
    if (trimmed && !currentValue.includes(trimmed)) {
      updateValue([...currentValue, trimmed])
      setInputValue('')
    }
  }, [inputValue, currentValue, updateValue])

  const removeChip = useCallback((index) => {
    if (readOnly) return
    const newValue = currentValue.filter((_, i) => i !== index)
    updateValue(newValue)
  }, [currentValue, readOnly, updateValue])

  const handleKeyDown = (e) => {
    if (disabled || readOnly) return
    if (e.key === 'Enter') {
      e.preventDefault()
      addChip()
    } else if (e.key === 'Backspace' && !inputValue && currentValue.length > 0) {
      removeChip(currentValue.length - 1)
    }
  }

  const handleWrapperClick = () => {
    if (!disabled && !readOnly && inputRef.current) {
      inputRef.current.focus()
    }
  }

  return (
    <Wrapper onClick={handleWrapperClick} {...rest}>
      {currentValue.map((chip, index) => (
        <Chip key={`${chip}-${index}`}>
          <ChipLabel>{chip}</ChipLabel>
          {!disabled && !readOnly && (
            <ChipRemoveButton
              type="button"
              aria-label={`Remove ${index + 1}`}
              onClick={(e) => {
                e.stopPropagation()
                removeChip(index)
              }}
            >
              &times;
            </ChipRemoveButton>
          )}
        </Chip>
      ))}
      <Input
        ref={inputRef}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={currentValue.length === 0 ? placeholder : ''}
        disabled={disabled}
        readOnly={readOnly}
        name={name}
      />
    </Wrapper>
  )
}

ChipsInput.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func,
  defaultValue: PropTypes.array,
  placeholder: PropTypes.string,
  freeSolo: PropTypes.bool,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  name: PropTypes.string,
}
